import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config';
import { getFilterTranslations } from '../i18n/filters';
import { readEventStream } from '../utils/readEventStream';
import { settleResultsReport } from '../utils/searchResultsReport';
import { splitProductFilter } from '../utils/galleryParams';

const buildChips = (filters, translations) => Object.entries(filters || {}).flatMap(([field, value]) => {
  if (field === 'createdat') return [{ field, label: `${translations[field] || 'Période'} ${value.startDate} → ${value.endDate}` }];
  if (value === true) return [{ field, label: translations[field] || field }];
  return (Array.isArray(value) ? value : []).map(option => ({
    field,
    value: option.value,
    label: `${translations[field] || field} · ${option.label}`
  }));
});

/**
 * What is really happening while a stage lasts, beyond the one line the server sent.
 *
 * The stream only speaks when a step ends, and the longest step by far is the model call: measured
 * between 2,6 and 7,7 s, during which the panel used to sit frozen on « Repérage des valeurs
 * connues ». These say what that wait is actually made of rather than inventing progress.
 */
const STAGE_NARRATION = {
  understanding: ['Reading your question...', 'Working out which dimensions it names...'],
  loading_vocabulary: ['Reading the values you have access to...', 'Only the programmes you may see...'],
  anchoring: ['Matching your words to real values...', 'The model is choosing among them...',
    'Composing the filters...'],
  grounding: ['Composing the filters...', 'Checking each value really exists...'],
  candidates: ['Widening the shortest lists...', 'The model is having a second look...'],
  interpreting: ['Reading your question...', 'Working out which dimensions it names...'],
  interpreted: ['Checking each value really exists...'],
  resolving_filter: ['Looking for the closest values...'],
  applying_filters: ['Almost there...']
};

const NARRATION_INTERVAL_MS = 2200;

/** Below this many photos, widening the period is worth more than narrowing it. */
const EXTEND_BELOW = 50;
/** A wider period is only offered if it adds at least this many days before the current one. */
const EXTEND_MIN_DAYS = 7;
/** The period shortcuts the plan offers, against the sentence shown for each. */
const PERIOD_LABELS = {
  'last-30-days': 'Limit to the last 30 days',
  'last-90-days': 'Limit to the last 90 days',
  'remove-period': 'Remove the period'
};
const PERIOD_SUGGESTIONS = Object.keys(PERIOD_LABELS);

const pad = value => `${value}`.padStart(2, '0');
const isoDate = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const shiftIsoDate = (iso, days) => {
  const date = new Date(`${iso}T00:00:00`);
  date.setDate(date.getDate() + days);
  return isoDate(date);
};

/**
 * The periods a thin result could widen to, smallest widening first.
 *
 * Only those that start at least a week before the current period: « the last 3 months » next to
 * « the last 90 days » would be an offer to change nothing. The end never moves back — a period
 * that already runs to the end of the year keeps it.
 */
const widerPeriods = (current, t, today = new Date()) => {
  if (!current?.startDate) return [];
  const endDate = current.endDate && current.endDate > isoDate(today) ? current.endDate : isoDate(today);
  const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
  const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
  const yearStart = new Date(today.getFullYear(), 0, 1);
  const latestUseful = shiftIsoDate(current.startDate, -EXTEND_MIN_DAYS);
  return [
    { id: 'extend-3-months', label: t('Last 3 months'), start: isoDate(threeMonthsAgo) },
    { id: 'extend-quarter', label: t('Current quarter'), start: isoDate(quarterStart) },
    { id: 'extend-year', label: t('Current year'), start: isoDate(yearStart) }
  ]
    .filter(option => option.start <= latestUseful)
    .sort((left, right) => right.start.localeCompare(left.start))
    .map(option => ({ id: option.id, label: option.label, patch: { createdat: { startDate: option.start, endDate } } }))
    .concat([{ id: 'extend-no-date', label: t('No date limit'), remove: ['createdat'] }]);
};

const formatSeconds = milliseconds => `${(milliseconds / 1000).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`;

const chipClass = 'rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-800 transition hover:border-blue-300 hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/50 dark:text-blue-200 dark:hover:bg-blue-900/50';

/**
 * Natural-language search bar, sitting above the picture grid and aligned with it.
 *
 * Deliberately one line at rest and at most three once a plan comes back: the photos are the
 * subject of this page, and an interpretation panel that pushed the first row of thumbnails off
 * screen was costing more than it explained. The counters it used to display — photos, active
 * filters, unresolved terms — are all already on screen: the total sits in the toolbar, the active
 * filters are the chips themselves, and unresolved terms are worth a line only when there are any.
 */
export default function IntelligentSearchPanel({
  fetchWithAuth,
  onApplyFilters,
  onReset,
  resultCount,
  resultsLoading,
  resultsFailure = null,
  defaultDateRange,
  onExport = null,
  canRate = false,
  canFilterAi = false,
  asClient = false
}) {
  const { t } = useTranslation();
  const translations = useMemo(() => ({ ...getFilterTranslations(t), createdat: t('Period') }), [t]);
  const [query, setQuery] = useState('');
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(null);
  const [narrationStep, setNarrationStep] = useState(0);
  const [refinements, setRefinements] = useState([]);
  const reportedLogRef = useRef(null);
  const awaitingResultsRef = useRef(null);
  const refinementRef = useRef(null);
  const searchStartedAtRef = useRef(null);
  const interpretedAtRef = useRef(null);
  const [elapsed, setElapsed] = useState(null);
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [commentSent, setCommentSent] = useState(false);
  const [ratingError, setRatingError] = useState('');

  // The server speaks only when a step ends; between two of its messages the line advances on its
  // own so the wait reads as work in progress rather than as a freeze.
  useEffect(() => {
    setNarrationStep(0);
    if (!loading) return undefined;
    const timer = setInterval(() => setNarrationStep(step => step + 1), NARRATION_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [loading, progress?.stage]);

  const recordGesture = useCallback((type, field = null, detail = {}) => {
    const logId = plan?.log_id;
    if (!logId) return;
    fetchWithAuth(`${API_BASE_URL}/api/intelligent-search/logs/${logId}/events`, {
      method: 'POST',
      body: JSON.stringify({ type, field, detail })
    }).catch(() => { /* journalling must never disturb a search */ });
  }, [plan?.log_id, fetchWithAuth]);

  useEffect(() => {
    const logId = plan?.log_id;
    const report = settleResultsReport({
      logId,
      applicable: plan?.applicable,
      reported: reportedLogRef.current,
      awaiting: awaitingResultsRef.current,
      loading: resultsLoading,
      failure: resultsFailure,
      count: resultCount
    });
    if (report.action === 'wait') awaitingResultsRef.current = logId;
    if (report.action !== 'count' && report.action !== 'failure') return;
    reportedLogRef.current = logId;
    if (searchStartedAtRef.current !== null) {
      const now = performance.now();
      const interpretedAt = interpretedAtRef.current ?? now;
      setElapsed({
        total: now - searchStartedAtRef.current,
        interpretation: interpretedAt - searchStartedAtRef.current,
        gallery: now - interpretedAt
      });
      searchStartedAtRef.current = null;
    }
    if (report.action === 'failure') {
      recordGesture('results_failed', null, { code: report.code });
      return;
    }
    fetchWithAuth(`${API_BASE_URL}/api/intelligent-search/logs/${logId}/results`, {
      method: 'POST',
      body: JSON.stringify({ count: report.count })
    }).catch(() => { /* journalling must never disturb a search */ });
  }, [plan, resultCount, resultsLoading, resultsFailure, fetchWithAuth, recordGesture]);

  // Une nouvelle recherche efface l'avis précédent : la note porte sur une réponse, pas sur le
  // panneau.
  useEffect(() => {
    setRating(null);
    setComment('');
    setCommentSent(false);
    setRatingError('');
  }, [plan?.log_id]);

  // Le pouce bas est enregistré au clic, sans attendre le commentaire : un avis qu'on ne détaille
  // pas reste un avis, et le perdre serait le pire des deux.
  const sendRating = async (value, text = null) => {
    const logId = plan?.log_id;
    if (!logId) return;
    setRatingError('');
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/intelligent-search/logs/${logId}/rating`, {
        method: 'POST',
        body: JSON.stringify({ rating: value, comment: text })
      });
      if (!response.ok) throw new Error('rating refused');
      setRating(value);
      if (text) setCommentSent(true);
    } catch (_) {
      setRatingError(t('Rating not saved'));
    }
  };

  const applyPlan = (nextFilters, nextPlan = plan) => {
    setPlan(nextPlan ? {
      ...nextPlan,
      filters: nextFilters,
      chips: buildChips(nextFilters, translations)
    } : null);
    onApplyFilters(nextFilters);
  };

  const runSearch = async () => {
    const normalized = query.normalize('NFKC').trim();
    if (normalized.length < 2 || loading) return;
    setLoading(true);
    setError('');
    setElapsed(null);
    searchStartedAtRef.current = performance.now();
    interpretedAtRef.current = null;
    setProgress({ stage: 'interpreting' });
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/intelligent-search/interpret-stream${asClient ? '?as_client=true' : ''}`, {
        method: 'POST',
        body: JSON.stringify({ query: normalized })
      });
      const payload = await readEventStream(response, (event, data) => {
        if (event === 'progress') setProgress(data);
      });
      interpretedAtRef.current = performance.now();
      if (payload.applicable === false) {
        searchStartedAtRef.current = null;
        setPlan({
          ...payload,
          filters: {},
          chips: []
        });
        return;
      }
      // The model names a product once; the gallery keeps its declared and its observed side apart.
      const initialFilters = splitProductFilter(payload.filters.createdat
        ? payload.filters
        : { ...payload.filters, createdat: defaultDateRange }, { isAdmin: canFilterAi });
      applyPlan(initialFilters, payload);
    } catch (searchError) {
      searchStartedAtRef.current = null;
      setError(searchError.message || t('Unable to interpret this search.'));
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const serverMessage = useMemo(() => {
    if (!progress) return '';
    if (progress.stage === 'interpreting') return t('Understanding your request...');
    if (progress.stage === 'interpreted') return t('Request understood; checking available filters...');
    if (progress.stage === 'resolving_filter') {
      return t('Resolving filter...', { filter: translations[progress.field] || progress.field });
    }
    if (progress.stage === 'filter_resolved') {
      return t('Filter values found', { count: progress.match_count, filter: translations[progress.field] || progress.field });
    }
    if (progress.stage === 'looking_up_stores') return t('Looking up authorized stores...');
    if (progress.stage === 'store_candidates') {
      return t('Store candidates found', {
        count: progress.match_count,
        suffix: progress.truncated ? '+' : '',
        stores: progress.samples?.join(', ') || '—'
      });
    }
    if (progress.stage === 'applying_filters') return t('Applying filters...');
    if (progress.stage === 'understanding') return t('Understanding your request...');
    if (progress.stage === 'loading_vocabulary') return t('Reading the values you have access to...');
    if (progress.stage === 'anchoring') return t('Spotting known values in your request...');
    if (progress.stage === 'grounding') return t('Matching your words to real values...');
    if (progress.stage === 'candidates') return t('Looking for the closest values...');
    return t('Interpreting...');
  }, [progress, t, translations]);

  // The server's own line comes first — it is the only one that is certainly true at that instant —
  // then the narration for that stage, then round again.
  const progressMessage = useMemo(() => {
    if (!serverMessage) return '';
    const narration = STAGE_NARRATION[progress?.stage] || [];
    const cycle = [serverMessage, ...narration.map(line => t(line))];
    return cycle[narrationStep % cycle.length];
  }, [serverMessage, progress?.stage, narrationStep, t]);

  // Asked for only once the photos are on screen: it is an offer, never a step of the search. A
  // failure leaves the panel exactly as it was.
  const loadRefinements = useCallback(async (filters, resultCount) => {
    const token = {};
    refinementRef.current = token;
    setRefinements([]);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/intelligent-search/refinements${asClient ? '?as_client=true' : ''}`, {
        method: 'POST',
        body: JSON.stringify({ filters, result_count: resultCount })
      });
      const payload = await response.json();
      if (refinementRef.current === token) setRefinements(payload?.suggestions || []);
    } catch (_) { /* a refinement nobody waited for is worth no error */ }
  }, [fetchWithAuth, asClient]);

  useEffect(() => {
    // After the photos, not beside them: the total they bring is what decides whether « latest
    // visit » can be offered at all, and the offer is only worth anything once it is known.
    if (loading || resultsLoading || !plan?.filters || plan.applicable === false) return;
    loadRefinements(plan.filters, resultCount);
  }, [loading, resultsLoading, resultCount, plan?.filters, plan?.applicable, loadRefinements]);

  // A thin result asks for a wider period, not a narrower one.
  const extendOptions = useMemo(() => {
    if (!plan?.filters || plan.applicable === false || resultsLoading || !(Number(resultCount) < EXTEND_BELOW)) return [];
    return widerPeriods(plan.filters.createdat, t);
  }, [plan?.filters, plan?.applicable, resultsLoading, resultCount, t]);

  // Measured values first: they carry a count, so they are chosen knowing what they hold. The
  // period shortcuts stay behind them — they need no query and are always applicable — unless the
  // result is thin enough to be offered wider periods instead.
  const refineOptions = useMemo(() => [
    ...refinements.map(suggestion => ({
      ...suggestion,
      label: suggestion.kind === 'latest_visit'
        ? t('Latest visit per store ({{stores}} stores, {{visits}} visits)', { stores: suggestion.stores, visits: suggestion.visits })
        : `${translations[suggestion.field] || suggestion.field} · ${suggestion.value}`
    })),
    ...(plan?.suggestions || [])
      .filter(suggestion => !extendOptions.length || !PERIOD_SUGGESTIONS.includes(suggestion.id))
      .map(suggestion => (PERIOD_LABELS[suggestion.id] ? { ...suggestion, label: t(PERIOD_LABELS[suggestion.id]) } : suggestion))
  ], [refinements, plan?.suggestions, extendOptions.length, translations, t]);

  const removeChip = (chip) => {
    if (!plan) return;
    recordGesture('chip_removed', chip.field, { value: chip.value, label: chip.label });
    const nextFilters = { ...plan.filters };
    if (chip.value === undefined) {
      delete nextFilters[chip.field];
    } else {
      const remaining = (nextFilters[chip.field] || []).filter(option => `${option.value}` !== `${chip.value}`);
      if (remaining.length) nextFilters[chip.field] = remaining;
      else delete nextFilters[chip.field];
    }
    applyPlan(nextFilters);
  };

  const resolveClarification = (clarification, candidate) => {
    if (!plan) return;
    recordGesture('clarification_resolved', candidate.field, {
      term: clarification.term, value: candidate.value, label: candidate.label
    });
    const existing = plan.filters[candidate.field] || [];
    const nextFilters = {
      ...plan.filters,
      [candidate.field]: [...existing, { value: candidate.value, label: candidate.label }]
    };
    applyPlan(nextFilters, {
      ...plan,
      clarifications: plan.clarifications.filter(item => item !== clarification)
    });
  };

  const dismissClarification = (clarification) => {
    if (!plan) return;
    recordGesture('clarification_dismissed', clarification.field, { term: clarification.term });
    setPlan({ ...plan, clarifications: plan.clarifications.filter(item => item !== clarification) });
  };

  const applySuggestion = (suggestion) => {
    if (!plan) return;
    recordGesture(
      'suggestion_applied',
      suggestion.field || Object.keys(suggestion.patch || {})[0] || suggestion.remove?.[0] || null,
      { id: suggestion.id, value: suggestion.value }
    );
    const nextFilters = { ...plan.filters, ...(suggestion.patch || {}) };
    (suggestion.remove || []).forEach(field => delete nextFilters[field]);
    applyPlan(nextFilters);
  };

  const startNewSearch = () => {
    recordGesture('search_reset');
    setQuery('');
    setPlan(null);
    setError('');
    if (plan?.applicable !== false) onReset();
  };

  const unresolved = plan?.unresolved_terms || [];
  const hasStatusRow = Boolean(error || loading || plan);

  return (
    <section className="mb-4 overflow-hidden rounded-xl border border-blue-200 bg-white dark:border-blue-900/70 dark:bg-gray-900">
      <div className="flex items-center gap-2 p-2">
        <span
          className="shrink-0 pl-1 text-blue-600 dark:text-blue-400"
          title={t('Intelligent search')}
          aria-hidden="true"
        >
          ✦
        </span>
        <span className="hidden shrink-0 items-center gap-2 lg:flex">
          <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">{t('Intelligent search')}</span>
          <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider text-blue-600 dark:bg-blue-950 dark:text-blue-300">Bêta</span>
          <span className="h-5 w-px bg-gray-200 dark:bg-gray-700" />
        </span>

        <input
          type="text"
          value={query}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              runSearch();
            }
          }}
          maxLength={1000}
          aria-label={t('Intelligent search')}
          className="min-w-0 flex-1 bg-transparent px-1 text-sm text-gray-900 outline-none placeholder:text-gray-400 dark:text-white"
          placeholder={t('Example intelligent search')}
        />

        {(query || plan) && !loading && (
          <button
            type="button"
            onClick={startNewSearch}
            aria-label={t('Clear the search')}
            title={t('Clear the search')}
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs leading-none text-gray-600 transition hover:bg-gray-300 hover:text-gray-900 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white"
          >
            ×
          </button>
        )}

        <button
          type="button"
          onClick={runSearch}
          disabled={loading || query.trim().length < 2}
          className="shrink-0 rounded-lg bg-blue-600 px-3.5 py-1.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? t('Searching...') : t('Search')}
        </button>
      </div>

      {hasStatusRow && (
        <div className="space-y-1.5 border-t border-gray-100 px-2.5 py-2 dark:border-gray-800">
          {error && <p className="text-xs text-red-700 dark:text-red-300">{error}</p>}

          {loading && progressMessage && (
            <p className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300">
              <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-blue-500" />
              <span key={progressMessage} className="intelligent-search-thinking">{progressMessage}</span>
            </p>
          )}

          {plan && !loading && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
              {plan.chips.length > 0 && <span className="text-xs text-gray-500 dark:text-gray-400">{t('Inferred filters')} :</span>}
              {plan.chips.map(chip => (
                <button key={`${chip.field}-${chip.value ?? chip.label}`} type="button" onClick={() => removeChip(chip)} className={chipClass}>
                  {chip.label} <span className="ml-0.5 text-blue-400">×</span>
                </button>
              ))}

              {plan.applicable === false && (
                <span className="text-xs text-amber-700 dark:text-amber-300">{t('No applicable filter was found.')}</span>
              )}
              {plan.applicable !== false && resultsLoading && (
                <span className="text-xs text-gray-500 dark:text-gray-400">{t('Updating results...')}</span>
              )}
              {plan.applicable !== false && !resultsLoading && Number(resultCount) === 0 && (
                <span className="text-xs text-amber-700 dark:text-amber-300">{t('No results match the applied filters.')}</span>
              )}
              {unresolved.length > 0 && (
                <span className="text-xs text-amber-700 dark:text-amber-300">{t('Not applied')} : {unresolved.join(' · ')}</span>
              )}
            </div>
          )}

          {plan?.clarifications?.length > 0 && !loading && plan.clarifications.map(clarification => (
            <div key={`${clarification.field}-${clarification.term}`} className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-2.5 py-1.5 dark:bg-amber-950/30">
              <span className="text-xs text-amber-900 dark:text-amber-200">
                {clarification.reason === 'outside_settled_context'
                  ? t('“{{term}}” does not exist for what you asked for — nothing was applied:', { term: clarification.term })
                  : t('“{{term}}” could mean several things — nothing was applied:', { term: clarification.term })}
              </span>
              {clarification.candidates.map(candidate => (
                <button
                  key={`${candidate.field}-${candidate.value}`}
                  type="button"
                  onClick={() => resolveClarification(clarification, candidate)}
                  className="rounded-full border border-amber-400 px-2.5 py-0.5 text-xs text-amber-900 hover:bg-amber-100 dark:border-amber-600 dark:text-amber-100 dark:hover:bg-amber-900/40"
                >
                  {translations[candidate.field] || candidate.field} · {candidate.label}
                </button>
              ))}
              <button type="button" onClick={() => dismissClarification(clarification)} className="text-xs text-amber-700 underline dark:text-amber-300">
                {t('Ignore')}
              </button>
            </div>
          ))}

          {plan?.warnings?.length > 0 && !loading && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              {t('Some dimensions could not be searched: {{fields}}', {
                fields: [...new Set(plan.warnings.map(warning => translations[warning.field] || warning.field))].join(', ')
              })}
            </p>
          )}

          {plan?.empty_combination && !loading && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              {t('Each of these values exists, but never on the same photo. Try removing one:')}
            </p>
          )}

          {extendOptions.length > 0 && !loading && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('Extend')} :</span>
              {extendOptions.map(suggestion => (
                <button key={suggestion.id} type="button" onClick={() => applySuggestion(suggestion)} className="rounded-full border border-dashed border-emerald-300 px-2.5 py-1 text-xs text-emerald-700 transition hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-950/30">
                  + {suggestion.label}
                </button>
              ))}
            </div>
          )}

          {refineOptions.length > 0 && !loading && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('Refine')} :</span>
              {refineOptions.map(suggestion => (
                <button key={suggestion.id} type="button" onClick={() => applySuggestion(suggestion)} className="rounded-full border border-dashed border-blue-300 px-2.5 py-1 text-xs text-blue-700 transition hover:bg-blue-50 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-950/30">
                  + {suggestion.label}
                  {suggestion.count !== undefined && (
                    <span className="ml-1 text-blue-500/70 dark:text-blue-400/70">
                      {suggestion.count.toLocaleString()}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {plan?.log_id && !loading && (
            <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2 dark:border-gray-800">
              {canRate && (
                <>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{t('Was this answer right?')}</span>
                  <button
                    type="button" onClick={() => sendRating(1)} aria-pressed={rating === 1} title={t('Right')}
                    className={`rounded-md p-1 transition ${rating === 1
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
                      : 'text-gray-400 hover:bg-gray-100 hover:text-emerald-600 dark:hover:bg-gray-800'}`}
                  >
                    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                      <path d="M1 8.25a1.25 1.25 0 1 1 2.5 0v7.5a1.25 1.25 0 1 1-2.5 0v-7.5ZM11 3V1.7c0-.268.14-.526.395-.607A2 2 0 0 1 14 3c0 .995-.182 1.948-.514 2.826-.204.54.166 1.174.744 1.174h2.52c1.243 0 2.261 1.01 2.146 2.247a23.864 23.864 0 0 1-1.341 5.974C17.153 16.323 16.072 17 14.9 17h-3.192a3 3 0 0 1-1.341-.317l-2.734-1.366A3 3 0 0 0 6.292 15H5V8h.963c.685 0 1.258-.483 1.612-1.068a4.011 4.011 0 0 1 2.166-1.73c.432-.143.853-.386 1.011-.814.16-.432.248-.9.248-1.388Z" />
                    </svg>
                  </button>
                  <button
                    type="button" onClick={() => sendRating(-1)} aria-pressed={rating === -1} title={t('Wrong')}
                    className={`rounded-md p-1 transition ${rating === -1
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300'
                      : 'text-gray-400 hover:bg-gray-100 hover:text-amber-600 dark:hover:bg-gray-800'}`}
                  >
                    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                      <path d="M18.905 12.75a1.25 1.25 0 1 1-2.5 0v-7.5a1.25 1.25 0 0 1 2.5 0v7.5ZM8.905 17v1.3c0 .268-.14.526-.395.607A2 2 0 0 1 5.905 17c0-.995.182-1.948.514-2.826.204-.54-.166-1.174-.744-1.174h-2.52c-1.242 0-2.26-1.01-2.146-2.247.193-2.08.652-4.082 1.341-5.974C2.752 3.678 3.833 3 5.005 3h3.192a3 3 0 0 1 1.341.317l2.734 1.366A3 3 0 0 0 13.613 5h1.292v7h-.963c-.685 0-1.258.483-1.612 1.068a4.01 4.01 0 0 1-2.166 1.73c-.432.143-.853.386-1.011.814-.16.432-.248.9-.248 1.388Z" />
                    </svg>
                  </button>
                  {(rating === 1 || commentSent) && <span className="text-xs text-emerald-600 dark:text-emerald-400">{t('Thanks, noted.')}</span>}
                  {ratingError && <span className="text-xs text-red-600 dark:text-red-400">{ratingError}</span>}
                </>
              )}
              {onExport && resultCount > 0 && (
                <button
                  type="button"
                  onClick={() => onExport(query.normalize('NFKC').trim() || null)}
                  className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h18v14H3V5zm0 4h18M9 5v14M15 5v14" />
                  </svg>
                  {t('Export to Excel')}
                </button>
              )}
              {elapsed && (
                <span
                  className={`${onExport && resultCount > 0 ? '' : 'ml-auto '}text-xs tabular-nums text-gray-400 dark:text-gray-500`}
                  title={t('From your request to the results: interpretation {{interpretation}}, gallery {{gallery}}', {
                    interpretation: formatSeconds(elapsed.interpretation),
                    gallery: formatSeconds(elapsed.gallery)
                  })}
                >
                  ⏱ {formatSeconds(elapsed.total)}
                </span>
              )}
            </div>
          )}

          {canRate && plan?.log_id && !loading && rating === -1 && !commentSent && (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <textarea
                rows={2} value={comment} onChange={event => setComment(event.target.value)}
                placeholder={t('Detail what the search should have found')}
                className="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs text-gray-800 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
              />
              <button
                type="button" disabled={!comment.trim() || commentSent}
                onClick={() => sendRating(-1, comment.trim())}
                className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 dark:disabled:bg-gray-700"
              >
                {t('Send')}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
